/*
Skeleton loaders. CSS-only shimmer (no framer-motion / no JS
animation) — a single linear-gradient background animated via
keyframes does the work. Three variants cover the shapes the SPA
needs: a plain text line, a row matching the table-row height, and
a card title.

Usage examples:
  <Skeleton variant="title" />
  <Skeleton variant="text" widthPercent={60} />
  <SkeletonRows count={6} />     // table-row stack

The widthPercent prop lets a caller render staggered widths so a
"text-block" placeholder doesn't look like a uniform brick.
*/

import type { JSX } from "react";

export interface SkeletonProps
{
	variant?:		"text" | "title" | "row";
	widthPercent?:	number;	// 0..100, defaults to 100
	style?:			React.CSSProperties;
}

export function Skeleton(props: SkeletonProps): JSX.Element
{
	const vClass = `skeleton skeleton--${props.variant ?? "text"}`;
	const vWidth = `${props.widthPercent ?? 100}%`;

	return <div className={vClass} style={{ width: vWidth, ...props.style }} aria-hidden="true" />;
}

/*
Renders N skeleton "rows" matching the table-row height. Used as
the inside of a paginated-table loading state to preserve layout so
the page doesn't jump when the data lands.
*/
export function SkeletonRows(
	props: { count: number }
): JSX.Element
{
	const vRows: JSX.Element[] = [];	// Output array

	for (let vI = 0; vI < props.count; vI++)
	{
		// Vary the width slightly per row so the placeholder doesn't
		// look like a flat brick of identical bars.
		const vWidth = 70 + ((vI * 13) % 30);

		vRows.push(<Skeleton key={vI} variant="row" widthPercent={vWidth} />);
	}

	return <>{vRows}</>;
}
